import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { chiama } from './cliente.js';

/**
 * Le anagrafiche di supporto.
 *
 * Il servizio non espone trenta rotte diverse ma una sola forma: qui c'è il
 * client che la usa, e l'interfaccia si costruisce leggendo i campi invece
 * di conoscerli.
 */

export interface VoceHub {
  key: string;
  label: string;
  group: string;
  description: string;
  readOnly: boolean;
  perCompany: boolean;
  count: number;
}

export type TipoCampo = 'testo' | 'testoLungo' | 'numero' | 'decimale' | 'siNo' | 'scelta';

export interface CampoDati {
  key: string;
  label: string;
  type: TipoCampo;
  required: boolean;
  inList: boolean;
  mono: boolean;
  help: string | null;
  values: { value: string; label: string }[] | null;
}

export interface MetaDati {
  key: string;
  label: string;
  group: string;
  description: string;
  readOnly: boolean;
  readOnlyReason: string | null;
  archivable: boolean;
  fields: CampoDati[];
}

export type RigaDati = Record<string, unknown> & { id: number | string; archived?: boolean };

interface Pagina {
  rows: RigaDati[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export function useHub() {
  return useQuery({
    queryKey: ['dati-generici'],
    queryFn: () => chiama<{ entries: VoceHub[] }>('/api/reference-data'),
  });
}

export function useMetaDati(chiave: string) {
  return useQuery({
    queryKey: ['dati-meta', chiave],
    queryFn: () => chiama<MetaDati>(`/api/reference-data/${chiave}/meta`),
    enabled: chiave.length > 0,
    staleTime: 60 * 60_000,
  });
}

export function useRigheDati(chiave: string, q: string, archiviati: boolean) {
  return useQuery({
    queryKey: ['dati-righe', chiave, q, archiviati],
    queryFn: () =>
      chiama<Pagina>(
        `/api/reference-data/${chiave}?size=500${q ? `&q=${encodeURIComponent(q)}` : ''}${
          archiviati ? '&archived=true' : ''
        }`,
      ),
    enabled: chiave.length > 0,
    placeholderData: (precedente) => precedente,
  });
}

/** Le tre scritture condividono l'invalidazione: cambia solo il verbo. */
function usaScrittura<T>(chiave: string, chiamata: (dati: T) => Promise<unknown>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: chiamata,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['dati-righe', chiave] });
      // il conteggio nell'indice cambia insieme alle righe
      void client.invalidateQueries({ queryKey: ['dati-generici'] });
    },
  });
}

export function useSalvaRigaDati(chiave: string) {
  return usaScrittura(chiave, ({ id, ...dati }: Record<string, unknown> & { id?: number | string }) =>
    id === undefined
      ? chiama<{ id: number }>(`/api/reference-data/${chiave}`, { metodo: 'POST', corpo: dati })
      : chiama<{ id: number }>(`/api/reference-data/${chiave}/${id}`, {
          metodo: 'PATCH',
          corpo: dati,
        }),
  );
}

export function useArchiviaRigaDati(chiave: string) {
  return usaScrittura(chiave, (id: number | string) =>
    chiama<{ archived: boolean }>(`/api/reference-data/${chiave}/${id}`, { metodo: 'DELETE' }),
  );
}

export function useRipristinaRigaDati(chiave: string) {
  return usaScrittura(chiave, (id: number | string) =>
    chiama<{ restored: boolean }>(`/api/reference-data/${chiave}/${id}/restore`, {
      metodo: 'POST',
    }),
  );
}
